/**
 * The Catalog search box focuses its query on one scope. Each scope maps the
 * typed text onto exactly one catalog API filter key — the scoped key and
 * `textSearch` are mutually exclusive (only one is ever sent).
 */
export type SearchScope = 'all' | 'artist' | 'title' | 'release';

export const DEFAULT_SEARCH_SCOPE: SearchScope = 'all';

/** Dropdown options (order + labels shown in the scope selector). */
export const SEARCH_SCOPE_OPTIONS: { value: SearchScope; label: string }[] = [
  { value: 'all', label: 'All Fields' },
  { value: 'artist', label: 'Artist Name' },
  { value: 'title', label: 'Title' },
  { value: 'release', label: 'Release' },
];

/** The single catalog filter key each scope writes its query into. */
const SCOPE_FILTER_KEY: Record<SearchScope, string> = {
  all: 'textSearch',
  artist: 'artistNameContains',
  title: 'titleContains',
  release: 'releaseNameContains',
};

/**
 * Catalog filter keys owned exclusively by the scoped-search control — every
 * scope's key except the `all` scope's `textSearch`. Derived from
 * SCOPE_FILTER_KEY so the two can never drift out of sync.
 */
export const SCOPED_SEARCH_KEYS: string[] = (
  Object.keys(SCOPE_FILTER_KEY) as SearchScope[]
)
  .filter((scope) => scope !== 'all')
  .map((scope) => SCOPE_FILTER_KEY[scope]);

/** Short label used in the active-search chip for a specific scope. */
const SCOPE_CHIP_LABEL: Record<Exclude<SearchScope, 'all'>, string> = {
  artist: 'Artist',
  title: 'Title',
  release: 'Release',
};

export const isSearchScope = (v: string | null): v is SearchScope =>
  v === 'all' || v === 'artist' || v === 'title' || v === 'release';

/**
 * The single filter-key patch a scoped query contributes to the request.
 * A blank/whitespace query contributes nothing.
 */
export function searchFilterPatch(
  scope: SearchScope,
  query: string,
): Record<string, string> {
  if (query.trim() === '') return {};
  return { [SCOPE_FILTER_KEY[scope]]: query };
}

/**
 * Active-chip label for the current search. `all` keeps the quoted "Search"
 * style; a specific scope reads like the old field filters (e.g. "Artist: x").
 */
export function searchChipLabel(scope: SearchScope, query: string): string {
  if (scope === 'all') return `Search: "${query}"`;
  return `${SCOPE_CHIP_LABEL[scope]}: ${query}`;
}
